/**
 * Lesson content model (docs/ref/content-schema.md, schema 1.0). Only the fields the app
 * reads are typed; unknown extra fields are allowed and ignored.
 */

export type Format = 'hsk2' | 'hsk3' | 'evolve' | 'reader';
export type TargetLang = 'zh-CN' | 'en-US' | 'tr-TR';

export interface Token {
  surface: string;
  pinyin?: string;
  ru?: string;
  wordId?: string | null;
  grammarId?: string;
  punct?: true;
}

export interface Sentence {
  id: string;
  paragraph?: string;
  speaker?: string;
  text: string;
  pinyin?: string;
  ru: string;
  newWordIds?: string[];
  tokens: Token[];
  tokensStatus: 'done' | 'pending_import';
}

export interface LessonText {
  id: string;
  kind: string;
  title?: string;
  sentences: Sentence[];
}

export interface Word {
  id: string;
  n?: number;
  word: string;
  pinyin?: string;
  ipa?: string;
  pos: string;
  ru: string;
  hskLevel?: number | null;
  cefr?: string;
  isNew: boolean;
  exampleSentenceId?: string | null;
}

export type ItemType =
  | 'multiple_choice'
  | 'choose_both_allowed'
  | 'fill_blank'
  | 'fill_from_bank'
  | 'fill_table'
  | 'choose_position'
  | 'match'
  | 'order_words'
  | 'comprehension'
  | 'label_image'
  | 'dictation'
  | 'complete_sentence'
  | 'complete_dialogue'
  | 'write_sentences'
  | 'picture_sentence'
  | 'retell_with_keywords'
  | 'free_list'
  | 'speak_prompt'
  | 'translate_ru_to_target';

export interface Item {
  id: string;
  type?: ItemType;
  prompt: string;
  promptRu?: string;
  options?: string[];
  answer?: string;
  answers?: string[] | string[][];
  word?: string;
  keyword?: string;
  keywords?: string[];
  autoCheck?: boolean;
}

export interface GrammarExample {
  zh?: string;
  en?: string;
  ru: string;
  fromTextSentenceId?: string;
}

export interface GrammarPoint {
  id: string;
  kind: string;
  title: string;
  pattern?: string;
  explanation_ru: string;
  examples: GrammarExample[];
  discovery?: Item[];
  accuracyCheck?: { rule: string; wrong: string; right: string };
  practice?: Item[];
}

export interface Collocation {
  pattern: string;
  head: string;
  partners: string[];
  headPosition?: 'left' | 'right';
}

export interface Distinction {
  id: string;
  pair: [string, string];
  common_ru: string;
  differences: { ru: string }[];
  practice: Item[];
}

export interface Exercise {
  id: string;
  bookNumber?: number | string;
  type: ItemType;
  instruction_zh?: string;
  instruction?: string;
  instruction_ru?: string;
  bank?: string[];
  autoCheck: boolean;
  items?: Item[];
}

export interface Expansion {
  id: string;
  topic_zh: string;
  topic_ru: string;
  words: { word: string; pinyin: string; ru: string }[];
  practice?: Omit<Exercise, 'id'> & { id?: string };
}

export interface Task {
  id: string;
  kind: 'speaking' | 'writing';
  source: string;
  topic_zh?: string;
  prompt_zh?: string;
  instruction?: string;
  questions?: string[];
  words?: string[];
  usefulPhrases?: string[];
  minChars?: number;
}

export type PlanStep = [string, number];

export interface AppPlan {
  plan: 'hsk2_3day' | 'hsk3_2day' | 'evolve_session' | 'reader';
  days?: { day: number; name: string; steps: PlanStep[] }[];
  steps?: PlanStep[];
  passRule?: { miniTestItems: number; minScore: number };
}

export interface Lesson {
  schemaVersion: '1.0';
  copyrightNote: string;
  id: string;
  format: Format;
  targetLang: TargetLang;
  number: number | string;
  title: { text: string; pinyin?: string; ru: string; en?: string };
  texts: LessonText[];
  words: Word[];
  grammar: GrammarPoint[];
  collocations?: Collocation[];
  distinctions?: Distinction[];
  exercises: Exercise[];
  expansion?: Expansion[];
  tasks: Task[];
  appSteps: AppPlan;
}

export function isChinese(lesson: Pick<Lesson, 'targetLang'>): boolean {
  return lesson.targetLang === 'zh-CN';
}

export function allSentences(lesson: Pick<Lesson, 'texts'>): Sentence[] {
  return lesson.texts.flatMap((t) => t.sentences);
}

export function findSentence(lesson: Pick<Lesson, 'texts'>, id: string): Sentence | undefined {
  return allSentences(lesson).find((s) => s.id === id);
}

export function findWord(lesson: Pick<Lesson, 'words'>, id: string): Word | undefined {
  return lesson.words.find((w) => w.id === id);
}

/** Short language code used for cards and courses ("zh", "en", "tr"). */
export function langCode(target: TargetLang): 'zh' | 'en' | 'tr' {
  return target === 'zh-CN' ? 'zh' : target === 'tr-TR' ? 'tr' : 'en';
}
